import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'

import { HBox, Box } from 'app/views/core/Box'
import { VScroll } from 'app/views/core/Scroll'

import { parseSubmissionDetails } from '../TestOverview/util'
import TestSubmissionList from '../TestOverview/TestSubmissionList'
import TestSubmissionDetails from '../TestOverview/TestSubmissionDetails'

import CodeReviewRestoreModal from './CodeReviewRestoreModal'

import { connect } from 'react-redux'
import * as sbcrsubmission from 'app/state/modules/sbcrsubmission'

function parseCodeReviewSubmissionDetails(submission) {
  // Ordinary lessons have a set of tests defined in the db's 'test' table, so we build
  // the list of test stages procedurally and identify a failed by its name (e.g.
  // '3-sqli-login-sqli-chars') to know which stage the failure occurred at. Coding
  // Challenges don't have data-driven tests; their tests just resolve to one of three
  // magic strings: syntax_error, functionality_broken, or vulnerability_not_fixed.
  // Since this may very well be temporary, we'll just hardcode these three stages and
  // then modify our submission details to use those error strings in place of the
  // actual test names, so that we can reuse the existing (newer) UI code as-is.
  let details = parseSubmissionDetails(submission)
  if (details.failedStage) {
    if (['syntax_error', 'functionality_broken', 'vulnerability_not_fixed'].includes(details.failedStage.errorMessage)) {
      details.failedStage.name = details.failedStage.errorMessage
    }
  }
  return details
}

function CodeReviewSubmissionOverview(props) {
  const { submissions, revertToSubmission } = props
  const [selectedSubmissionId, setSelectedSubmissionId] = useState(-1)

  // When we get a new code submission, automatically select it
  useEffect(() => {
    if (submissions.length > 0) {
      setSelectedSubmissionId(submissions[0].id)
    }
  }, [submissions.length])

  // Allow the user to click a button in the detail view to restore the selected code
  // submission to the sandbox, with a confirmation prompt
  const [wantsToRestore, setWantsToRestore] = useState(false)

  const selectedSubmission = selectedSubmissionId === -1 ? null : submissions.find((x) => x.id === selectedSubmissionId)
  const details = selectedSubmission ? parseCodeReviewSubmissionDetails(selectedSubmission) : null
  return (
    submissions.length === 0 ? (
      <Box fillParent style={{ padding: 20 }}>
        <h3 className="uk-margin-remove-bottom">Test Submissions</h3>
        <span className="uk-text-small">No tests have been submitted.</span>
      </Box>
    ) : (
      <HBox fillParent>
        <Box fixed={250}>
          <VScroll>
            <TestSubmissionList
              submissions={submissions}
              selectedSubmissionId={selectedSubmissionId}
              onSelect={setSelectedSubmissionId}
            />
          </VScroll>
        </Box>
        <Box grow>
          <VScroll>
            {details && (
              <TestSubmissionDetails
                onRequestCode={() => setWantsToRestore(true)}
                requestCodeButtonLabel="Revert to this submission"
                stages={[
                  {
                    name: 'syntax_error',
                    title: 'Syntax Check / 500 Errors',
                    description: 'Does your code build and run without errors? Note that syntax errors will not affect your score.',
                  },
                  {
                    name: 'functionality_broken',
                    title: 'Functionality Check',
                    description: 'Did your changes break the existing functionality of the app?',
                  },
                  {
                    name: 'vulnerability_not_fixed',
                    title: 'Vulnerability Check',
                    description: 'Does your code fix the vulnerability?'
                  },
                ]}
                {...details}
              />
            )}
          </VScroll>
          {details && wantsToRestore && (
            <CodeReviewRestoreModal
              submittedAt={details.submittedAt}
              onClose={() => setWantsToRestore(false)}
              onConfirm={() => {
                setWantsToRestore(false)
                revertToSubmission(details.submissionId)
              }}
            />
          )}
        </Box>
      </HBox>
    )
  )
}
CodeReviewSubmissionOverview.propTypes = {
  submissions: PropTypes.arrayOf(PropTypes.object).isRequired,
  revertToSubmission: PropTypes.func.isRequired,
}

export default connect((state) => {
  const shouldShowSubmission = (submission) => submission.test.passed || submission.test.name || !submission.stale
  return {
    submissions: state.sbcrsubmission.submissions.filter(shouldShowSubmission),
  }
}, (dispatch) => ({
  revertToSubmission: (submissionId) => dispatch(sbcrsubmission.revertToSubmission(submissionId)),
}))(CodeReviewSubmissionOverview)
